/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useRef,
  useCallback,
} from "react";
import { useAppSelector, useAppDispatch } from "../../../shared/redux/hooks";
import { LIST_OF_PERFORMANCE_DATA } from "../constants";
import "./add_accounts.scss";
import {
  addPerformanceData,
  setPerformanceData,
} from "../../../shared/redux/action/performance.action";
import {
  Button,
  SpinnerLoader,
  Toastify,
  Label,
  Input,
  SingleSelect,
  SheetFooter,
  Switch,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  MultiSelectCheckboxTag,
} from "lift-ui";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { cloneDeep } from "lodash";
import apiClient from "@lift-admin/utils/apiClient";
import {
  buildSummary,
  diffList,
  formatIdWithName,
} from "@lift-admin/utils/logChangeFormatter";
import { actionLog } from "../../../App";
import { Info, Trash2 } from "lucide-react";
import TagInput from "../../../shared/components/tag-input";

export interface AddPerformanceFormRef {
  handleStepClick: (step: number) => void;
}

interface AddPerformanceFormProps {
  calledFromWhichComponent: string;
  brands: any[];
  initialBrand?: { id: number; name: string } | null;
  // eslint-disable-next-line no-unused-vars
  onStepChange: (step: number, completedSteps: number[]) => void;
  onClose: () => void;
  isOpen: boolean;
}

const CARD_TEMPLATE: any = {
  id: null,
  formal_name: "",
  display_name: "",
  account_id: "",
  account_name: "",
  property_name: "",
  property_id: "",
  selected_conversion_event: "",
  selected_conversion_tag_names: [],
  attribution_window: "",
  notes: "",
  attribution_basis: "",
  original_currency: "",
  is_mbm: false,
  is_emq: false,
  is_active: true,
  modified_by: "",
  last_modified_column: [],
  emq_primary_conversion: "",
  emq_secondary_conversion: "",
  emq_tertiary_conversion: "",
  source_of_truth: false,
  is_automation_enabled: false,
  account_config: "",
  keyword_level_needed: "No",
  is_enterprise_client: "Campaign Level UTM",
};

const AddPerformanceForm = forwardRef<
  AddPerformanceFormRef,
  AddPerformanceFormProps
>(({ calledFromWhichComponent, brands, initialBrand, onStepChange, onClose, isOpen }, ref) => {
  const dispatch = useAppDispatch();
  const { performanceData } = useAppSelector((state) => state.performance);
  const user = JSON.parse(localStorage.getItem("user") || "");
  const isMediaPlatformAccounts = calledFromWhichComponent?.toLowerCase() === "media platform accounts";

  // ---- State ----
  const [selectedBrand, setSelectedBrand] = useState<any>(null);
  const [formResetKey, setFormResetKey] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localPlatFormData, setLocalPlatFormData] = useState<any[]>([]);
  const [totalUsedJsonData, setTotalUsedJsonData] = useState<any[]>([]);
  const [dataSources, setDataSources] = useState<string[]>([]);

  // ---- Effects ----

  useEffect(() => {
    const names = LIST_OF_PERFORMANCE_DATA.map((item) => item.display_name);
    setDataSources(names);
  }, []);

  useEffect(() => {
    if (initialBrand && brands.length > 0) {
      const match = brands.find((b: any) => b.id === initialBrand.id);
      setSelectedBrand(match ?? { id: initialBrand.id, name: initialBrand.name });
      setFormResetKey((prev) => prev + 1);
    }
  }, [initialBrand, brands]);

  // ---- Imperative handle ----
  useImperativeHandle(ref, () => ({
    handleStepClick: (step: number) => { /* kept for parent compat */ },
  }));

  // ---- Step sync ----
  const getCompletedSteps = useCallback(() => {
    return selectedBrand && localPlatFormData.length > 0 ? [0] : [];
  }, [selectedBrand, localPlatFormData]);

  const prevStepRef = useRef(1);
  const prevCompletedRef = useRef<number[]>([]);

  useEffect(() => {
    const completed = getCompletedSteps();
    if (prevStepRef.current !== 1 || JSON.stringify(prevCompletedRef.current) !== JSON.stringify(completed)) {
      prevStepRef.current = 1;
      prevCompletedRef.current = completed;
      onStepChange(1, completed);
    }
  }, [getCompletedSteps, onStepChange]);

  // ---- Reset ----
  const resetFormState = () => {
    setLocalPlatFormData([]);
    setTotalUsedJsonData([]);
    setSelectedBrand(null);
    setFormResetKey((prev) => prev + 1);
    setIsSubmitting(false);
  };

  // ===========================================================================
  // FIELD UPDATE — all edits write directly to localPlatFormData[index]
  // ===========================================================================

  const updateField = (index: number, key: string, value: any) => {
    const newData = cloneDeep(localPlatFormData);
    const item = newData[index];

    // Meta Ads: only one account can be MBM at a time
    if (key === "is_mbm" && value === "Yes") {
      newData.forEach((d: any) => {
        if (d.formal_name === "Meta Ads") d.is_mbm = "No";
      });
      dispatch(setPerformanceData(
        performanceData.map((d: any) =>
          d.formal_name === "Meta Ads" ? { ...d, is_mbm: "No" } : d
        )
      ));
    }

    // Meta Ads: sync selected_conversion_event → emq_primary_conversion
    if (
      key === "selected_conversion_event" &&
      item?.formal_name === "Meta Ads"
    ) {
      item.emq_primary_conversion = item.is_emq === "Yes" ? value : "";
    }

    // EMQ toggle off → clear conversion fields
    if (key === "is_emq" && value === "No") {
      item.emq_primary_conversion = "";
      item.emq_secondary_conversion = "";
      item.emq_tertiary_conversion = "";
    }

    item[key] = value;
    setLocalPlatFormData(newData);
  };

  const updateNestedDropdown = (
    index: number,
    key: string,
    options: any[],
    selectedLabel: string
  ) => {
    const newData = cloneDeep(localPlatFormData);
    const item = newData[index];
    options.forEach((opt: any) => {
      if (typeof opt === "string" && opt === selectedLabel) {
        item[key] = selectedLabel;
      } else if (typeof opt === "object" && opt.label === selectedLabel) {
        item[key] = null;
      }
    });
    item.selectedDropdownValue = selectedLabel;
    setLocalPlatFormData(newData);
  };

  const updateMultipleDropdown = (
    index: number,
    key: string,
    subIndex: number,
    value: string,
    childrenLength: number
  ) => {
    const newData = cloneDeep(localPlatFormData);
    const item = newData[index];
    const currentVal = item[key];
    const arr = currentVal?.includes("/")
      ? currentVal.split("/")
      : new Array(childrenLength).fill(null);
    arr[subIndex] = value;
    item[key] = arr.join("/");
    setLocalPlatFormData(newData);
  };

  // ===========================================================================
  // PLATFORM MANAGEMENT — multi-select driven
  // ===========================================================================

  const buildPlatformCard = (displayName: string, id: number) => {
    const json = LIST_OF_PERFORMANCE_DATA.find((d) => d.display_name === displayName);
    if (!json) return null;

    const card: any = cloneDeep(CARD_TEMPLATE);
    card.id = id;
    card.display_name = json.display_name;
    card.formal_name = json.formal_name;

    if (card.display_name === "Client System" || card.display_name === "Other") {
      card.display_name = "";
    }

    Object.entries(json.fields).forEach(([key, field]: [string, any]) => {
      if (field.validation?.default) card[key] = field.validation.default;
    });

    if ((json as any).type) card.type = (json as any).type;
    if (card.formal_name === "Meta Ads") {
      card.selectedDropdownValue = card.attribution_window;
    }

    return { card, usedJson: json };
  };

  const handlePlatformMultiSelectChange = async (selectedNames: string[]) => {
    if (isMediaPlatformAccounts && !selectedBrand) return;

    const currentNames = localPlatFormData.map(
      (item: any) => item.display_name || item.formal_name
    );
    const toAdd = selectedNames.filter((n: string) => !currentNames.includes(n));
    const toRemove = currentNames.filter((n: string) => !selectedNames.includes(n));

    let nextData = localPlatFormData.filter(
      (item: any) => !toRemove.includes(item.display_name || item.formal_name)
    );
    let nextUsedJson = totalUsedJsonData.filter(
      (_: any, i: number) =>
        !toRemove.includes(localPlatFormData[i]?.display_name || localPlatFormData[i]?.formal_name)
    );

    const baseId = performanceData.length + nextData.length + 1;
    toAdd.forEach((name, i) => {
      const built = buildPlatformCard(name, baseId + i);
      if (built) {
        nextData = [...nextData, built.card];
        nextUsedJson = [...nextUsedJson, built.usedJson];
      }
    });

    setLocalPlatFormData(nextData);
    setTotalUsedJsonData(nextUsedJson);

    if (toAdd.length || toRemove.length) {
      const platformDiff = diffList(currentNames, selectedNames);
      const desc = {
        message: buildSummary(
          "Performance account form",
          selectedBrand?.name,
          "updated",
          (platformDiff.added.length ? 1 : 0) + (platformDiff.removed.length ? 1 : 0)
        ),
        category: "performance",
        action: "update_add_form_platforms",
        user_id: user.id,
        user_name: user.name,
        brand_id: selectedBrand?.id || null,
        brand_name: selectedBrand?.name || null,
        changes: [
          {
            label: "Platforms",
            added: platformDiff.added,
            removed: platformDiff.removed,
          },
        ],
      };
      await actionLog?.actionLogTrack(JSON.stringify(desc), false);
    }
  };

  const handleRemovePlatform = async (index: number) => {
    const removed = localPlatFormData[index];
    const nextData = localPlatFormData.filter((_: any, i: number) => i !== index);
    const nextUsedJson = totalUsedJsonData.filter((_: any, i: number) => i !== index);

    // Re-index IDs for consistency
    nextData.forEach((item: any, i: number) => {
      item.id = performanceData.length + i + 1;
    });

    setLocalPlatFormData(nextData);
    setTotalUsedJsonData(nextUsedJson);
    Toastify("success", "Platform removed successfully.");

    const removedName =
      removed?.display_name || removed?.formal_name || "N/A";
    const desc = {
      message: `Platform "${removedName}" removed from add form`,
      category: "performance",
      action: "remove_platform_from_form",
      user_id: user.id,
      user_name: user.name,
      brand_id: selectedBrand?.id || null,
      brand_name: selectedBrand?.name || null,
      changes: [
        {
          label: "Platforms",
          added: [],
          removed: [removedName],
        },
      ],
      account_name: removed?.account_name || null,
    };
    await actionLog?.actionLogTrack(JSON.stringify(desc), false);
  };

  // ===========================================================================
  // VALIDATION
  // ===========================================================================

  const isFieldRequired = (validation: any, item: any) => {
    if (typeof validation.required === "object") {
      const { dependent_field, required_if_empty } = validation.required;
      return required_if_empty && !item?.[dependent_field];
    }
    return validation.required;
  };

  const getPlatformLabel = (index: number, item: Record<string, any>) =>
    item?.display_name || item?.formal_name || totalUsedJsonData[index]?.display_name || totalUsedJsonData[index]?.formal_name || `Platform ${index + 1}`;

  const validateFields = (index: number, item: Record<string, any>) => {
    if (!item) return false;
    const fields = totalUsedJsonData[index]?.fields;
    if (!fields) return false;

    const platformLabel = getPlatformLabel(index, item);
    const prefix = (msg: string) => `${platformLabel}: ${msg}`;

    let requiredEmpty: string[] = [];

    for (const [key, fieldDef] of Object.entries(fields) as [string, any][]) {
      if (key === "emq_primary_conversion" && item.is_emq === "No") continue;

      // Either/or dependency
      if (typeof fieldDef.validation?.required === "object") {
        const dep = fieldDef.validation.required.dependent_field;
        if (item[key] === "" && (item[dep]?.length ?? 0) === 0) {
          Toastify("error", prefix(`Please provide either ${key.replace(/_/g, " ")} or ${dep.replace(/_/g, " ")}.`));
          return true;
        }
      }

      if (fieldDef.validation?.required && typeof fieldDef.validation.required !== "object") {
        requiredEmpty.push(key);
      }

      // Pattern — only check when field has a value
      const val = item[key];
      if (fieldDef.validation?.pattern && val !== "" && val != null) {
        const regex = new RegExp(fieldDef.validation.pattern[0]);
        if (!regex.test(String(val))) {
          Toastify("error", prefix(fieldDef.validation.pattern[1]));
          return true;
        }
      }
    }

    requiredEmpty = requiredEmpty.filter((f) => item[f] === "" || item[f] === null);
    if (requiredEmpty.length > 0) {
      Toastify("error", prefix("Please fill in all required fields."));
      return true;
    }
    return false;
  };

  // ===========================================================================
  // SUBMIT
  // ===========================================================================

  const handleSubmit = async () => {
    // Validate all cards
    for (let i = 0; i < localPlatFormData.length; i++) {
      if (validateFields(i, localPlatFormData[i])) {
        const desc = {
          message: `Validation failed while adding performance accounts for brand "${selectedBrand?.name || "N/A"}"`,
          category: "performance",
          action: "validation_failed",
          user_id: user.id,
          user_name: user.name,
          brand_id: selectedBrand?.id || null,
          brand_name: selectedBrand?.name || null,
        };
        await actionLog?.actionLogTrack(JSON.stringify(desc), true);
        return;
      }
    }

    if (localPlatFormData.length === 0) return;

    const cleaned = localPlatFormData.map((data: any) => {
      const { selectedDropdownValue, ...rest } = data;
      return rest;
    });

    if (isMediaPlatformAccounts) {
      setIsSubmitting(true);
      const payload = cleaned.map((data: any) => {
        const configParts: string[] = [];
        if (data.keyword_level_needed === "Yes") configParts.push("keyword_level_needed=true");
        if (data.is_enterprise_client === "All UTMs Level") configParts.push("is_enterprise_client=true");

        const { keyword_level_needed, is_enterprise_client, ...restData } = data;
        return {
          ...restData,
          brand_id: selectedBrand.id,
          is_emq: data.is_emq === "Yes",
          is_mbm: data.is_mbm === "Yes",
          source_of_truth: data.source_of_truth === "Yes",
          is_automation_enabled: data.is_automation_enabled === "Yes",
          attribution_window: data.attribution_window?.includes("/") ? data.attribution_window : "Default",
          account_config: configParts.length > 0 ? configParts.join(";") : null,
        };
      });

      try {
        const res = await apiClient.post("/brand/v1/paid_ad_accounts/add", payload);
        const body = res.data;
        const results: any[] = body?.response ?? [];
        const successCount = body?.successCount ?? results.filter((r: any) => r.status === "success").length;
        const failedCount = body?.failedCount ?? results.filter((r: any) => r.status === "failed").length;

        if (successCount > 0) {
          Toastify("success", `${successCount} account${successCount > 1 ? "s" : ""} added successfully.`);
        }

        results
          .forEach((r: any, i: number) => {
            if (r.status !== "failed") return;
            const platformName = cleaned[i]?.display_name || cleaned[i]?.formal_name || `Platform ${i + 1}`;
            const accountLabel = r.account?.account_name || r.account?.display_name;
            const message = accountLabel
              ? `${platformName} (${accountLabel}): ${r.error || "Unknown error"}`
              : `${platformName}: ${r.error || "Unknown error"}`;
            Toastify("error", message);
          });

        // Keep only the failed cards; remove successful ones
        const failedIndices = new Set(
          results
            .map((r: any, i: number) => (r.status === "failed" ? i : -1))
            .filter((i: number) => i >= 0)
        );

        if (failedCount === 0) {
          onClose();
          resetFormState();
        } else {
          const remainingData = localPlatFormData.filter((_: any, i: number) => failedIndices.has(i));
          const remainingJson = totalUsedJsonData.filter((_: any, i: number) => failedIndices.has(i));
          remainingData.forEach((item: any, i: number) => { item.id = performanceData.length + i + 1; });
          setLocalPlatFormData(remainingData);
          setTotalUsedJsonData(remainingJson);
          setIsSubmitting(false);
        }

        const desc = {
          message: `Added ${successCount} performance account${successCount === 1 ? "" : "s"} to brand "${selectedBrand?.name || "N/A"}"${failedCount ? ` (${failedCount} failed)` : ""}`,
          status: res.status,
          method: res.config.method,
          url: res.config.url,
          category: "performance",
          action: "create_paid_ad_accounts",
          user_id: user.id,
          user_name: user.name,
          brand_id: selectedBrand?.id || null,
          brand_name: selectedBrand?.name || null,
          success_count: successCount,
          failed_count: failedCount,
          accounts: payload.map((acc: any) => ({
            account_id: acc.account_id,
            account_name: acc.account_name,
            platform: acc.display_name || acc.formal_name,
          })),
          details: [
            {
              label: "Brand ID",
              value: formatIdWithName(selectedBrand?.id, selectedBrand?.name),
            },
            { label: "Accounts Added", value: String(successCount) },
            { label: "Failed", value: String(failedCount) },
            ...payload.map((acc: any) => ({
              label: acc.display_name || acc.formal_name || "Account",
              value: `${acc.account_name} (${acc.account_id})`,
            })),
          ],
        };
        await actionLog?.actionLogTrack(
          JSON.stringify(desc),
          failedCount > 0 && successCount === 0
        );
      } catch (err: any) {
        setIsSubmitting(false);
        const errorDesc = {
          message: `Failed to add performance accounts for brand "${selectedBrand?.name || "N/A"}": ${err.response?.data?.message || err.message}`,
          status: err.response?.status || "Unknown",
          method: "POST",
          url: "/brand/v1/paid_ad_accounts/add",
          category: "performance",
          action: "create_paid_ad_accounts",
          user_id: user.id,
          user_name: user.name,
          brand_id: selectedBrand?.id || null,
          brand_name: selectedBrand?.name || null,
        };
        await actionLog?.actionLogTrack(JSON.stringify(errorDesc), true);
        Toastify("error", `Failed to add performance account: ${err.response?.data?.message ?? err.message ?? "Please try again."}`);
      }
    } else {
      onClose();
      dispatch(addPerformanceData(cleaned as any));
      resetFormState();
      Toastify("success", "Performance Added Successfully");
      const desc = {
        message: buildSummary(
          "Performance account",
          selectedBrand?.name,
          "created"
        ),
        category: "performance",
        action: "create_paid_ad_accounts",
        user_id: user.id,
        user_name: user.name,
        brand_id: selectedBrand?.id || null,
        brand_name: selectedBrand?.name || null,
        details: cleaned.map((acc: any) => ({
          label: acc.display_name || acc.formal_name || "Account",
          value: `${acc.account_name || "N/A"} (${acc.account_id || "N/A"})`,
        })),
      };
      await actionLog?.actionLogTrack(JSON.stringify(desc), false);
    }
  };

  const handleClose = async () => {
    resetFormState();
    onClose();
    const desc = {
      message: "Add Performance Account dialog closed without saving",
      category: "performance",
      action: "add_dialog_closed_without_saving",
      user_id: user.id,
      user_name: user.name,
      brand_id: selectedBrand?.id || null,
      brand_name: selectedBrand?.name || null,
    };
    await actionLog?.actionLogTrack(JSON.stringify(desc), false);
  };

  // ===========================================================================
  // RENDER
  // ===========================================================================

  const canAddPlatforms = !isMediaPlatformAccounts || !!selectedBrand;
  const isStepValid = !!selectedBrand && localPlatFormData.length > 0;

  return (
    <TooltipProvider>
      <div className="flex-1 min-h-0 overflow-y-auto py-6 w-full min-w-0">
        <div className="space-y-6 w-full min-w-0">

          {/* Brand Selection */}
          <div className="space-y-1.5">
            <Label>Brand *</Label>
            <SingleSelect
              key={formResetKey}
              options={brands.map((b: any) => ({ label: b.name, value: b.id }))}
              defaultValue={selectedBrand?.id || undefined}
              onChange={async (value: any) => {
                const brand = brands.find((b: any) => b.id === value);
                setSelectedBrand(brand);
                const desc = {
                  message: `Brand "${brand?.name || "N/A"}" selected in Add Performance wizard`,
                  category: "performance",
                  action: "brand_selected_in_wizard",
                  user_id: user.id,
                  user_name: user.name,
                  brand_id: brand?.id || null,
                  brand_name: brand?.name || null,
                  changes: [
                    {
                      label: "Brand",
                      from: selectedBrand?.name || "Not Set",
                      to: brand?.name || "Not Set",
                    },
                  ],
                };
                await actionLog?.actionLogTrack(JSON.stringify(desc), false);
              }}
              placeholder="Select brand..."
              containerClassName="w-full"
            />
          </div>

          {/* Platform Multi-Select */}
          <div className="space-y-1.5">
            <Label className="flex items-center gap-1">
              Select Platform(s)
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex cursor-help">
                    <Info className="h-3.5 w-3.5 text-muted-foreground" />
                  </span>
                </TooltipTrigger>
                <TooltipContent>Select one or more platforms to add their forms below.</TooltipContent>
              </Tooltip>
            </Label>
            {isMediaPlatformAccounts && !selectedBrand && (
              <p className="text-sm text-muted-foreground">Select a brand first to add ad accounts.</p>
            )}
            <div className={!canAddPlatforms ? "pointer-events-none opacity-60" : undefined}>
              <MultiSelectCheckboxTag
                placeholder={canAddPlatforms ? "Select platform(s)..." : "Select a brand first"}
                options={dataSources.map((name) => ({ value: name, label: name }))}
                value={localPlatFormData.map((item: any) => item.display_name || item.formal_name)}
                onChange={handlePlatformMultiSelectChange}
                containerClassName="w-full"
                showSelectAll
              />
            </div>
          </div>

          {/* Platform Cards */}
          <div className="space-y-6 w-full min-w-0">
            {localPlatFormData.map((item: any, index: number) => {
              const jsonDef = totalUsedJsonData[index];
              if (!jsonDef) return null;

              const entries = Object.entries(jsonDef.fields);
              const regularFields: [string, any][] = [];
              const switchFields: [string, any][] = [];
              const textareaFields: [string, any][] = [];
              const toggleDependentFields: [string, any][] = [];

              entries.forEach(([key, value]: [string, any]) => {
                if (value.validation?.allow_na || value.validation?.allow_tbd) return;
                const isConversion =
                  value.label === "Primary Conversion" ||
                  value.label === "Secondary Conversion" ||
                  value.label === "Tertiary Conversion";
                if (value.type === "switch") switchFields.push([key, value]);
                else if (value.type === "textarea") textareaFields.push([key, value]);
                else if (isConversion) toggleDependentFields.push([key, value]);
                else regularFields.push([key, value]);
              });

              const showConversionFields = item.is_emq === "Yes";

              return (
                <Card key={item.id ?? index} className="w-full min-w-0 rounded-lg border bg-card text-card-foreground shadow-sm">
                  <CardContent className="p-6 space-y-5">

                    {/* Card Header */}
                    <div className="flex items-center justify-between w-full gap-3">
                      <div className="text-lg font-semibold text-gray-900 min-w-0 truncate">
                        {item.display_name || item.formal_name}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive flex-shrink-0 ml-auto"
                        onClick={() => handleRemovePlatform(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>

                    {/* Free-form Display Name (Client System / Other) */}
                    {jsonDef.type === "Free Form Text" && (
                      <div className="space-y-1.5">
                        <Label htmlFor={`display_name_${index}`}>
                          Display Name <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id={`display_name_${index}`}
                          value={item.display_name || ""}
                          onChange={(e: any) => updateField(index, "display_name", e.target.value)}
                        />
                      </div>
                    )}

                    {/* Regular Fields — 2 column grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
                      {regularFields.map(([key, fieldDef]: [string, any]) => (
                        <React.Fragment key={key}>

                          {/* Text */}
                          {fieldDef.type === "text" && (
                            <div className="space-y-1.5">
                              <Label htmlFor={`${key}_${index}`}>
                                {fieldDef.label}
                                {isFieldRequired(fieldDef.validation, item) && <span className="text-red-500 ml-0.5">*</span>}
                              </Label>
                              <Input
                                id={`${key}_${index}`}
                                name={key}
                                value={item[key] ?? ""}
                                onChange={(e: any) => updateField(index, key, e.target.value)}
                              />
                            </div>
                          )}

                          {/* Dropdown */}
                          {fieldDef.type === "dropdown" && (
                            <div className="space-y-1.5">
                              <Label>{fieldDef.label}</Label>
                              <Select value={item[key] || ""} onValueChange={(val) => updateField(index, key, val)}>
                                <SelectTrigger className="w-full"><SelectValue placeholder={fieldDef.label} /></SelectTrigger>
                                <SelectContent>
                                  {fieldDef.options.map((opt: any, i: number) => (
                                    <SelectItem key={i} value={opt}>{opt}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}

                          {/* Multiple Dropdown */}
                          {fieldDef.type === "multiple-dropdown" &&
                            fieldDef.options.map((subItem: any) => (
                              <React.Fragment key={subItem?.label || "multi"}>
                                {subItem?.children?.map((sub: any, subIdx: number) => (
                                  <div key={subIdx} className="space-y-1.5">
                                    <Label>{sub.label}</Label>
                                    <Select
                                      value={item[key]?.split("/")[subIdx] || ""}
                                      onValueChange={(val) => updateMultipleDropdown(index, key, subIdx, val, subItem.children.length)}
                                    >
                                      <SelectTrigger className="w-full"><SelectValue placeholder={sub.label} /></SelectTrigger>
                                      <SelectContent>
                                        {sub.options?.map((d: any, di: number) => (
                                          <SelectItem key={di} value={d.value}>{d.value}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                ))}
                              </React.Fragment>
                            ))
                          }

                          {/* Nested Dropdown */}
                          {fieldDef.type === "nested-dropdown" && (
                            <>
                              <div className="space-y-1.5">
                                <Label>{fieldDef.label}</Label>
                                <Select
                                  value={
                                    item[key] && !item[key]?.includes("/")
                                      ? item[key]
                                      : item.selectedDropdownValue || ""
                                  }
                                  onValueChange={(val) => updateNestedDropdown(index, key, fieldDef.options, val)}
                                >
                                  <SelectTrigger className="w-full"><SelectValue placeholder={fieldDef.label} /></SelectTrigger>
                                  <SelectContent>
                                    {fieldDef.options.map((opt: any, i: number) => (
                                      <SelectItem key={i} value={opt.label ?? opt}>{opt.label ?? opt}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>

                              {/* Nested children */}
                              {fieldDef.options.map((nested: any, ni: number) => (
                                <React.Fragment key={`nested-${ni}`}>
                                  {typeof nested === "object" &&
                                    nested.label === item.selectedDropdownValue &&
                                    nested.children?.map((child: any, ci: number) => (
                                      <div key={ci} className="space-y-1.5">
                                        <Label>{child.label}</Label>
                                        <Select
                                          value={item[key]?.includes("/") ? item[key].split("/")[ci] || "" : ""}
                                          onValueChange={(val) => updateMultipleDropdown(index, key, ci, val, nested.children.length)}
                                        >
                                          <SelectTrigger className="w-full"><SelectValue placeholder={child.label} /></SelectTrigger>
                                          <SelectContent>
                                            {child.options.map((o: any, oi: number) => (
                                              <SelectItem key={oi} value={o.value}>{o.label}</SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                      </div>
                                    ))}
                                </React.Fragment>
                              ))}
                            </>
                          )}

                          {/* Array (tag input) */}
                          {fieldDef.type === "array" && (
                            <div className="col-span-1 md:col-span-1">
                              <TagInput
                                id={`${key}_${index}`}
                                label={fieldDef.label}
                                placeholder="Type and Enter (tag value)"
                                value={item[key] ?? []}
                                onChange={(newTags) => updateField(index, key, newTags)}
                                singleLine={key === "selected_conversion_tag_names"}
                              />
                            </div>
                          )}

                        </React.Fragment>
                      ))}
                    </div>

                    {/* Switches / Toggles */}
                    {switchFields.length > 0 && (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3 mt-5 pt-4 border-t border-gray-100">
                        {switchFields.map(([key, fieldDef]: [string, any]) => (
                          <div key={key} className="flex items-center justify-between gap-2 min-h-[36px]">
                            <div className="flex items-center gap-1">
                              <span className="text-sm font-medium leading-none">{fieldDef.label}</span>
                              {fieldDef.tooltip && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="inline-flex cursor-help">
                                      <Info className="h-3.5 w-3.5 text-muted-foreground" />
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent>{fieldDef.tooltip}</TooltipContent>
                                </Tooltip>
                              )}
                            </div>
                            <Switch
                              checked={item[key] === "Yes"}
                              onCheckedChange={(checked) => updateField(index, key, checked ? "Yes" : "No")}
                            />
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Toggle-dependent conversion fields */}
                    {toggleDependentFields.length > 0 && showConversionFields && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5 mt-5 pt-4 border-t border-gray-100">
                        {toggleDependentFields.map(([key, fieldDef]: [string, any]) => (
                          <div key={key} className="space-y-1.5">
                            <div className="flex items-center gap-1">
                              <Label htmlFor={`${key}_${index}`}>
                                {fieldDef.label}
                                {isFieldRequired(fieldDef.validation, item) && <span className="text-red-500 ml-0.5">*</span>}
                              </Label>
                              {fieldDef.iButton && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="inline-flex cursor-help">
                                      <Info className="h-3.5 w-3.5 text-muted-foreground" />
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent>Entered text should match the actual conversion value</TooltipContent>
                                </Tooltip>
                              )}
                            </div>
                            <Input
                              id={`${key}_${index}`}
                              name={key}
                              value={item[key] ?? ""}
                              onChange={(e: any) => updateField(index, key, e.target.value)}
                            />
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Textarea (Notes) */}
                    {textareaFields.length > 0 && (
                      <div className="mt-5 w-full min-w-0">
                        {textareaFields.map(([key, fieldDef]: [string, any]) => (
                          <div key={key} className="space-y-1.5 w-full min-w-0">
                            <Label htmlFor={`notes_${index}`}>Notes</Label>
                            <textarea
                              id={`notes_${index}`}
                              name="notes"
                              placeholder="Notes"
                              rows={4}
                              value={item[key] ?? ""}
                              onChange={(e) => updateField(index, key, e.target.value)}
                              className="w-full min-w-0 box-border border border-gray-200 rounded-md p-2.5 text-sm min-h-[100px] resize-y focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                            />
                          </div>
                        ))}
                      </div>
                    )}

                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>

      {/* Footer */}
      <SheetFooter className="border-t pt-4 flex-shrink-0">
        <div className="flex justify-end gap-2 w-full">
          <Button variant="outline" onClick={handleClose}>Close</Button>
          {isSubmitting ? (
            <Button disabled>
              <SpinnerLoader className="h-4 w-4 mr-2" />
              Submitting...
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={!isStepValid}>Submit</Button>
          )}
        </div>
      </SheetFooter>
    </TooltipProvider>
  );
});

AddPerformanceForm.displayName = "AddPerformanceForm";

export default AddPerformanceForm;
